import { useCallback, useContext, useMemo } from 'react'

import { trackClientEvent } from 'analytics/analytics'
import {
  getPropertyRatePackageUpgradeSelectedKey,
  type PropertyRateUpgradeCandidate,
  type PropertyRateUpgradeVariant,
} from 'components/OfferPage/offerPageUtils/propertyLoyaltyPackageUpgrade'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import noop from 'lib/function/noop'

interface PropertyRateStayUpgradeState {
  /** The refundability option the upgrade is currently applied to, if any. */
  appliedVariant: PropertyRateUpgradeVariant | undefined
  isApplied: boolean
  /**
   * Refundability options that can't be chosen while the upgrade is applied, because the upgrade
   * isn't offered on them. Empty unless applied.
   */
  lockedRateIds: Set<string>
  /**
   * The option Apply should land on: whichever one the customer has selected, when that option can
   * take the upgrade, otherwise the cheapest one that can. Undefined when nothing is upgradeable.
   */
  applyTargetRate: App.EnrichedPropertyRate | undefined
  applyUpgrade: (sourceRate: App.EnrichedPropertyRate) => void
  removeUpgrade: () => void
}

/**
 * Shared applied-state and apply/remove behaviour for a package option's loyalty stay upgrade, so the
 * call-out banner and the refundability selector act on one implementation rather than two.
 */
export default function usePropertyRateStayUpgrade(
  candidate: PropertyRateUpgradeCandidate | undefined,
  roomId: string,
  selectedRateId?: string,
  onApplyRateSelected: (rateId: string) => void = noop,
): PropertyRateStayUpgradeState {
  const offerPageDispatch = useOfferPageDispatch()
  const { loyaltyTargetStayUpgradesSelected } = useContext(OfferPageStateContext)

  // Any of the package's refundability options may be the applied one, since applying can move the
  // customer onto a dearer option when the upgrade isn't offered on the one they were viewing.
  const appliedVariant = useMemo(() => candidate?.variants.find(variant => {
    const key = getPropertyRatePackageUpgradeSelectedKey(variant.rate)
    return !!key && loyaltyTargetStayUpgradesSelected?.[key] === 'package'
  }), [candidate, loyaltyTargetStayUpgradesSelected])

  const isApplied = !!appliedVariant

  const lockedRateIds = useMemo(() => {
    if (!isApplied || !candidate) return new Set<string>()
    return new Set(candidate.variants.filter(variant => !variant.selectable).map(variant => variant.rate.rateId))
  }, [isApplied, candidate])

  // The customer's own refundability choice wins whenever it can carry the upgrade; the selection is
  // only moved when it has to be, which is when the option they are on isn't offered one.
  const applyTargetRate = useMemo(() => {
    if (!candidate) return undefined
    const selected = candidate.variants.find(variant => variant.rate.rateId === selectedRateId)
    return selected?.target ? selected.rate : candidate.sourceRate
  }, [candidate, selectedRateId])

  const applyUpgrade = useCallback((sourceRate: App.EnrichedPropertyRate) => {
    const key = getPropertyRatePackageUpgradeSelectedKey(sourceRate)
    if (!key) return
    // Apply may switch refundability, so select the upgrade's source rate in both the card and Trip
    // Planner snapshot.
    onApplyRateSelected(sourceRate.rateId)
    offerPageDispatch({
      type: OfferPageActions.setViewedRoomRateId,
      roomId,
      rateId: sourceRate.rateId,
    })
    // Only one option in a package can hold the upgrade. Switching refundability moves it, so clear
    // whichever option held it before — otherwise both stay applied and the row and banner disagree.
    const appliedKey = appliedVariant ? getPropertyRatePackageUpgradeSelectedKey(appliedVariant.rate) : undefined
    if (appliedKey && appliedKey !== key) {
      offerPageDispatch({
        type: OfferPageActions.toggleLoyaltyTargetRoomUpgrade,
        selectedUpgradeKey: appliedKey,
        selectedTargetUpgradeType: undefined,
      })
    }
    offerPageDispatch({
      type: OfferPageActions.toggleLoyaltyTargetRoomUpgrade,
      selectedUpgradeKey: key,
      selectedTargetUpgradeType: 'package',
    })
    trackClientEvent({
      subject: 'loyalty_package_upgrade',
      action: 'toggled_on',
      category: 'offer_page',
      type: 'interaction',
    })
  }, [offerPageDispatch, roomId, appliedVariant, onApplyRateSelected])

  const removeUpgrade = useCallback(() => {
    if (!appliedVariant) return
    const key = getPropertyRatePackageUpgradeSelectedKey(appliedVariant.rate)
    if (!key) return
    offerPageDispatch({
      type: OfferPageActions.toggleLoyaltyTargetRoomUpgrade,
      selectedUpgradeKey: key,
      selectedTargetUpgradeType: undefined,
    })
    trackClientEvent({
      subject: 'loyalty_package_upgrade',
      action: 'toggled_off',
      category: 'offer_page',
      type: 'interaction',
    })
  }, [appliedVariant, offerPageDispatch])

  return { appliedVariant, isApplied, lockedRateIds, applyTargetRate, applyUpgrade, removeUpgrade }
}
